import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";
import { getToken, onTokenChange } from "./tokenStore";

// Uma conexão só para o app inteiro.
//
// Antes cada tela chamava `io()` por conta própria. O socket.io-client 4 não
// reaproveita o Manager quando o namespace já existe no cache — ele abre um
// novo — então 22 telas viravam 22 WebSockets, e cada `disconnect()` de tela
// que desmontava derrubava um handshake ainda em andamento. Daí a enxurrada de
// "WebSocket is closed before the connection is established" no console.
//
// Aqui a conexão é única e contada por referência: cada consumidor recebe um
// controle que só enxerga os próprios ouvintes. Ao desmontar, ele remove os
// que registrou; a conexão real só cai quando o último consumidor sai.

let socketReal = null;
let tokenDaConexao = null;
let consumidores = 0;

// Avisa as telas de que o tempo real ficou surdo por um tempo (queda, celular
// que dormiu) e que elas precisam buscar de novo o que perderam: o socket só
// entrega o que acontece ENQUANTO está conectado, não o que passou antes.
const ouvintesRessincronia = new Set();
export const onRessincronizar = (ouvinte) => {
	ouvintesRessincronia.add(ouvinte);
	return () => ouvintesRessincronia.delete(ouvinte);
};
const pedirRessincronia = () => {
	ouvintesRessincronia.forEach((ouvinte) => {
		try {
			ouvinte();
		} catch {
			// Uma tela quebrada não pode impedir as outras de se atualizar.
		}
	});
};

let jaConectouAntes = false;
let tentativaDeVolta = 0;
let temporizadorDeVolta = null;
let reiniciarNoProximoToken = false;

// Derruba a conexão e abre outra. Necessário porque, num celular que dormiu,
// o WebSocket costuma estar morto sem o navegador ter avisado: `connected`
// continua true e nada chega.
const reiniciarConexao = () => {
	reiniciarNoProximoToken = false;
	if (!socketReal) return;
	socketReal.disconnect();
	socketReal.connect();
};

// Quando é o SERVIDOR que derruba (token vencido no handshake), o Socket.IO
// não tenta de novo sozinho — só reconecta automático nas quedas de rede. Foi
// o que deixava o app mudo até o F5: o token vencia durante o sono, o servidor
// recusava a volta e ninguém mais tentava.
const voltarDepoisDeRecusa = () => {
	if (temporizadorDeVolta || !socketReal) return;

	const espera = Math.min(30_000, 2_000 * 2 ** tentativaDeVolta);
	tentativaDeVolta += 1;

	temporizadorDeVolta = setTimeout(() => {
		temporizadorDeVolta = null;
		if (socketReal && !socketReal.connected) socketReal.connect();
	}, espera);
};

// Separa o endereço da API em origem + caminho do Socket.IO.
//
// Quando a API mora num subcaminho ("https://dominio/api/"), passar a URL
// inteira para o io() não funciona: o socket.io-client lê o caminho como
// *namespace*, não como path do transporte, e o servidor responde "Invalid
// namespace". O caminho tem que ir na opção `path`.
//
// Com a API na raiz ("http://localhost:8080/") o resultado é "/socket.io" —
// exatamente o padrão de antes.
const enderecoDoSocket = () => {
	const base = String(getBackendUrl() || "");

	try {
		const url = new URL(base, window.location.origin);
		const prefixo = url.pathname.replace(/\/+$/, "");

		return { origem: url.origin, path: `${prefixo}/socket.io` };
	} catch {
		return { origem: base, path: "/socket.io" };
	}
};

const lerToken = () => getToken();

// Sem token não há o que ouvir: o backend recusa o handshake e o cliente fica
// tentando de novo. É o caso da tela de login.
const controleVazio = {
	on: () => controleVazio,
	off: () => controleVazio,
	emit: () => controleVazio,
	disconnect: () => {}
};

// Mantém o handshake da próxima reconexão com o token válido.
//
// O token só é verificado no handshake. Sem isto, uma reconexão depois dos 15
// minutos de validade — queda de rede, notebook que dormiu, servidor que
// reiniciou — repetia o token vencido, era recusada, e o cliente tentava de
// novo com o mesmo token para sempre: a tela parava de receber QR Code,
// mensagem nova e mudança de status, sem nenhum aviso.
const aplicarToken = (token) => {
	if (!socketReal || !token) return;

	tokenDaConexao = token;

	if (socketReal.io?.opts?.query) {
		socketReal.io.opts.query.token = token;
	}
	if (socketReal.auth) {
		socketReal.auth.token = token;
	}

	// Voltou do segundo plano e a renovação do token acabou de chegar: agora
	// sim dá para reabrir a conexão com um token que o servidor aceita.
	if (reiniciarNoProximoToken) reiniciarConexao();
};

function connectToSocket() {
	const token = lerToken();

	if (!token) return controleVazio;

	// Token diferente com ninguém pendurado significa outra sessão: descarta a
	// conexão antiga. Com consumidores ativos não se mexe — o backend só valida
	// o token no handshake, e derrubar a conexão aqui deixaria a lista de
	// tickets e as notificações mudas até a tela remontar. A renovação de token
	// acontece a cada poucos minutos; ela não pode custar a conexão de todos.
	if (socketReal && tokenDaConexao !== token && consumidores <= 0) {
		socketReal.disconnect();
		socketReal = null;
	}

	// Conexão viva com token renovado: só alinha o que vai no próximo
	// handshake, sem derrubar a atual. (O caminho normal é o ouvinte do
	// tokenStore lá embaixo; isto cobre quem monta entre uma renovação e a
	// outra.)
	if (socketReal && tokenDaConexao !== token) {
		aplicarToken(token);
	}

	if (!socketReal) {
		tokenDaConexao = token;
		const { origem, path } = enderecoDoSocket();

		socketReal = openSocket(origem, {
			path,
			transports: ["websocket", "polling"],
			query: { token }
		});

		socketReal.on("connect", () => {
			tentativaDeVolta = 0;
			if (jaConectouAntes) pedirRessincronia();
			jaConectouAntes = true;
		});

		socketReal.on("disconnect", (motivo) => {
			if (motivo === "io server disconnect") voltarDepoisDeRecusa();
		});

		socketReal.on("connect_error", () => {
			// Recusa no handshake (token vencido) também para o auto-reconnect
			// em alguns casos; garante nova tentativa.
			if (socketReal && !socketReal.active) voltarDepoisDeRecusa();
		});
	}

	consumidores += 1;

	const meus = [];
	let ativo = true;

	const controle = {
		on(evento, handler) {
			if (!ativo) return controle;

			meus.push([evento, handler]);
			socketReal.on(evento, handler);

			// As telas entram nas salas do Socket.IO dentro do "connect"
			// (`socket.on("connect", () => socket.emit("joinChatBox", id))`).
			// Quando cada tela abria a própria conexão isso sempre acontecia;
			// com a conexão compartilhada, quem chega depois encontra o socket
			// já conectado e o evento nunca mais dispara — a tela não entra na
			// sala e para de receber atualização em tempo real. Dispara na
			// hora, num tick à parte para o handler não rodar antes de o
			// componente terminar de montar.
			if (evento === "connect" && socketReal.connected) {
				setTimeout(() => {
					if (ativo) handler();
				}, 0);
			}

			return controle;
		},
		off(evento, handler) {
			const i = meus.findIndex(([e, h]) => e === evento && h === handler);
			if (i >= 0) meus.splice(i, 1);
			if (socketReal) socketReal.off(evento, handler);
			return controle;
		},
		emit(...args) {
			if (ativo && socketReal) socketReal.emit(...args);
			return controle;
		},
		// Mantém o nome que as telas já usam. Não derruba a conexão dos outros:
		// só larga os ouvintes deste consumidor.
		disconnect() {
			if (!ativo) return;
			ativo = false;

			meus.forEach(([evento, handler]) => {
				if (socketReal) socketReal.off(evento, handler);
			});
			meus.length = 0;

			consumidores -= 1;

			if (consumidores <= 0 && socketReal) {
				socketReal.disconnect();
				socketReal = null;
				tokenDaConexao = null;
				consumidores = 0;
			}
		}
	};

	return controle;
}

// O tokenStore avisa sozinho quando o token muda (login, renovação, logout).
onTokenChange(aplicarToken);

// Celular que dormiu ou troca de app: ao voltar ao foco, o socket costuma ter
// caído. Força a reconexão na hora — com o token já renovado pelo useAuth — em
// vez de esperar o backoff. Sem isto, mensagem e status novos demoravam demais
// a aparecer no PWA.
if (typeof document !== "undefined") {
	let escondidoDesde = null;

	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "hidden") {
			escondidoDesde = Date.now();
			return;
		}

		if (!socketReal) return;

		const tempoFora = escondidoDesde ? Date.now() - escondidoDesde : 0;
		escondidoDesde = null;

		if (!socketReal.connected) {
			socketReal.connect();
			return;
		}

		// Ficou um tempo em segundo plano: o socket pode estar morto mesmo
		// "conectado". Reabre assim que o token renovado chegar (o useAuth
		// renova ao voltar); se a renovação não vier, reabre mesmo assim.
		if (tempoFora > 15_000) {
			reiniciarNoProximoToken = true;
			setTimeout(() => {
				if (reiniciarNoProximoToken) reiniciarConexao();
			}, 4_000);
			pedirRessincronia();
		}
	});

	window.addEventListener("online", () => {
		if (!socketReal) return;
		if (!socketReal.connected) socketReal.connect();
		pedirRessincronia();
	});
}

export default connectToSocket;
